<template>
  <div v-if="!dismissed && showPrompt" class="bg-white rounded-xl p-24 md:p-32 shadow-md">
    <!-- Loading State -->
    <div v-if="isCheckingEmail" class="text-center py-24">
      <fa
        class="text-muted text-2xl mb-16"
        :icon="['fal', 'spinner']"
        :spin="true"
      />
      <p class="text-muted text-sm">{{ $lang('checkout', 'post_order_checking_status') }}</p>
    </div>

    <!-- Registration Form -->
    <div v-else-if="!registered && !showLogin">
      <h3 class="text-heading-small mb-16">{{ $lang('checkout', 'post_order_create_account_title') }}</h3>
      <p class="text-standard text-muted mb-16">
        {{ $lang('checkout', 'post_order_create_account_text') }}
      </p>
      
      <div v-if="orderEmail" class="mb-16">
        <div class="text-sm text-muted mb-4">{{ $lang('checkout', 'post_order_your_email') }}</div>
        <div class="text-standard font-medium">{{ orderEmail }}</div>
      </div>

      <div class="bg-blue-25 px-16 py-12 rounded-regular mb-16">
        <p class="text-sm text-muted">
          {{ $lang('account', 'email_verification_message') }}
        </p>
      </div>

      <form class="space-y-12" @submit.prevent="handleRegister">
        <div v-if="error" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
          {{ error }}
        </div>

        <div class="flex gap-8">
          <button 
            type="submit" 
            class="btn btn--primary btn--md flex-1"
            :disabled="loading"
          >
            <span v-if="!loading">{{ $lang('checkout', 'post_order_create_account_button') }}</span>
            <fa
              v-else
              class="text-sm"
              :icon="['fal', 'spinner']"
              :spin="true"
            />
          </button>
          <button 
            type="button" 
            class="btn btn--secondary btn--md"
            @click="handleDismiss"
          >
            {{ $lang('checkout', 'post_order_not_now') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Login Form for Existing Customers -->
    <div v-else-if="showLogin && !registered">
      <h3 class="text-heading-small mb-16">{{ $lang('checkout', 'post_order_login_title') }}</h3>
      <p class="text-standard text-muted mb-16">
        {{ $lang('checkout', 'post_order_login_text') }}
      </p>
      
      <div v-if="orderEmail" class="mb-16">
        <div class="text-sm text-muted mb-4">{{ $lang('checkout', 'post_order_your_email') }}</div>
        <div class="text-standard font-medium">{{ orderEmail }}</div>
      </div>

      <form class="space-y-12" @submit.prevent="handleLogin">
        <div>
          <label class="block text-sm mb-8 text-standard font-medium">
            {{ $lang('checkout', 'post_order_password') }}
          </label>
          <TextInput 
            v-model="loginPassword"
            type="password"
            :placeholder="$lang('checkout', 'post_order_enter_password')"
            required
          />
        </div>

        <div v-if="error" class="text-warning text-sm bg-red-25 px-12 py-8 rounded-regular">
          {{ error }}
        </div>

        <div class="flex gap-8">
          <button 
            type="submit" 
            class="btn btn--primary btn--md flex-1"
            :disabled="loading || !loginPassword"
          >
            <span v-if="!loading">{{ $lang('checkout', 'post_order_login_button') }}</span>
            <fa
              v-else
              class="text-sm"
              :icon="['fal', 'spinner']"
              :spin="true"
            />
          </button>
          <button 
            type="button" 
            class="btn btn--secondary btn--md"
            @click="handleDismiss"
          >
            {{ $lang('checkout', 'post_order_not_now') }}
          </button>
        </div>

        <div class="text-center mt-12">
          <button 
            type="button"
            class="text-sm text-primary underline"
            @click="showForgotPassword = true"
          >
            {{ $lang('checkout', 'post_order_forgot_password') }}
          </button>
        </div>
      </form>
    </div>

    <div v-else class="text-center">
      <div class="inline-flex items-center justify-center w-48 h-48 rounded-full bg-green-25 text-success mb-16">
        <fa
          class="text-2xl"
          :icon="['fal', 'check']"
        />
      </div>
      <h3 class="text-heading-small mb-8">
        {{ didLogin ? $lang('checkout', 'post_order_login_success_title') : $lang('checkout', 'post_order_success_title') }}
      </h3>
      <p class="text-standard text-muted mb-16">
        {{ didLogin ? $lang('checkout', 'post_order_login_success_text') : $lang('account', 'email_verification_message') }}
      </p>
      <p v-if="ordersMigrated > 0" class="text-standard text-muted mb-16">
        {{ ordersLinkedMessage }}
      </p>
    </div>
  </div>

  <!-- Forgot Password Modal -->
  <ForgotPasswordModal 
    v-model="showForgotPassword" 
    :initial-email="orderEmail"
  />
</template>

<script setup lang="ts">
import TextInput from '~/components/forms/TextInput.vue';
import ForgotPasswordModal from '~/components/account/ForgotPasswordModal.vue';

const props = defineProps<{
  orderEmail?: string;
  orderId?: string;
  orderToken?: string;
}>();

const route = useRoute();
const { $lang } = useNuxtApp();
const locale = computed(() => {
  const pathSegments = route.path.split('/').filter(Boolean);
  return pathSegments[0] || 'se';
});

const loginPassword = ref('');
const error = ref('');
const loading = ref(false);
const registered = ref(false);
const dismissed = ref(false);
const ordersMigrated = ref(0);
const showLogin = ref(false);
const showForgotPassword = ref(false);
const didLogin = ref(false); // Track if user logged in vs registered

// Check if user is logged in
const isLoggedIn = ref(false);
const isCheckingEmail = ref(true); // Start as true to show loading initially

// Only show prompt if we have an order email (guest checkout completed) AND user is not logged in
const showPrompt = computed(() => !!props.orderEmail && !isLoggedIn.value);

// Computed property for orders linked message
const ordersLinkedMessage = computed(() => {
  if (ordersMigrated.value > 0) {
    const template = $lang('checkout', 'post_order_orders_linked');
    return template.replace('{{count}}', String(ordersMigrated.value));
  }
  return '';
});

onMounted(async () => {
  try {
    // Account creation and order linking must run even when the prompt was dismissed.
    const result = await $fetch<{
      success: boolean;
      alreadyLoggedIn: boolean;
      alreadyRegistered: boolean;
      ordersMigrated: number;
    }>('/api/centra/auto-register-silent', {
      method: 'POST',
      body: postOrderRequest(),
    });
    if (!result.success) {
      throw new Error('Post-order account preparation did not succeed');
    }
    isLoggedIn.value = result.alreadyLoggedIn;
    showLogin.value = result.alreadyRegistered;
    ordersMigrated.value = result.ordersMigrated;

    const storageKey = props.orderId ? `postOrderRegistered_${props.orderId}` : 'postOrderRegistered';
    const dismissKey = props.orderId ? `postOrderDismissed_${props.orderId}` : 'postOrderDismissed';
    dismissed.value = !!(sessionStorage.getItem(storageKey) || sessionStorage.getItem(dismissKey));
  } catch (err: unknown) {
    reportAccountError(err);
  } finally {
    isCheckingEmail.value = false;
  }
});

function postOrderRequest() {
  return {
    email: props.orderEmail,
    locale: locale.value,
    orderId: props.orderId,
    orderToken: props.orderToken,
  };
}

function reportAccountError(err: unknown) {
  const errorObj = err as {
    data?: { data?: { code?: string; step?: string; centraStatus?: number; centraError?: string } };
  };
  console.error('[POST ORDER REG] Account operation failed:', {
    status: errorObj?.data?.data?.centraStatus,
    step: errorObj?.data?.data?.step,
    centraError: errorObj?.data?.data?.centraError || 'No Centra error details available',
  });
  error.value = $lang('account', errorObj?.data?.data?.code === 'activation_email_failed'
    ? 'failed_send_reset_email' : 'registration_failed');
}

async function handleRegister() {
  error.value = '';
  loading.value = true;

  try {
    const res = await $fetch<{ success: boolean; alreadyLoggedIn: boolean; ordersMigrated: number }>('/api/centra/activate-account', {
      method: 'POST',
      body: postOrderRequest(),
    });

    if (res.alreadyLoggedIn) {
      isLoggedIn.value = true;
      return;
    }
    if (res.success) {
      registered.value = true;
      ordersMigrated.value = res.ordersMigrated;
      
      // Store in session storage so we don't show again for this order
      if (import.meta.client) {
        const storageKey = props.orderId ? `postOrderRegistered_${props.orderId}` : 'postOrderRegistered';
        sessionStorage.setItem(storageKey, 'true');
      }
    } else {
      throw new Error('Account activation did not succeed');
    }
  } catch (err: unknown) {
    reportAccountError(err);
  } finally {
    loading.value = false;
  }
}

async function handleLogin() {
  error.value = '';
  loading.value = true;

  try {
    const res = await $fetch<any>('/api/centra/login', {
      method: 'POST',
      body: {
        email: props.orderEmail,
        password: loginPassword.value,
      },
    });

    if (res.success) {
      registered.value = true;
      didLogin.value = true; // Track that this was a login, not registration
      // Order is automatically linked because session is updated
      ordersMigrated.value = 1; // At least the current order
      
      if (import.meta.client) {
        const storageKey = props.orderId ? `postOrderRegistered_${props.orderId}` : 'postOrderRegistered';
        sessionStorage.setItem(storageKey, 'true');
      }
    } else {
      // Handle case where res.success is false
      error.value = $lang('account', 'invalid_credentials');
    }
  } catch (err: unknown) {
    console.error('[POST ORDER LOGIN] Login error:', err);
    const errorObj = err as {
      status?: number;
      statusCode?: number;
      response?: { status?: number };
      data?: { statusCode?: number };
    };
    const status = errorObj?.response?.status ?? errorObj?.statusCode ??
      errorObj?.status ?? errorObj?.data?.statusCode;

    error.value = status === 401 || status === 406
      ? $lang('account', 'invalid_credentials')
      : $lang('account', 'login_failed');
  } finally {
    loading.value = false;
  }
}

function handleDismiss() {
  dismissed.value = true;
  // Store in session storage so we don't show again during this session for this order
  if (import.meta.client) {
    const dismissKey = props.orderId ? `postOrderDismissed_${props.orderId}` : 'postOrderDismissed';
    sessionStorage.setItem(dismissKey, 'true');
  }
}
</script>
